<template>
  <div class="step-progress__wrapper">
    <div class="step-progress__wrapper-before" :style="{'background-color': passiveColor, height: lineThickness + 'px'}"></div>
    <div class="step-progress__bar">
      <div
        v-for="(step, index) in steps"
        :style="{
          '--activeColor': activeColor,
          '--currentStepColor': currentStepColor || activeColor,
          '--passiveColor': passiveColor,
          '--activeBorder': activeThickness + 'px',
          '--passiveBorder': passiveThickness + 'px'
        }"
        :class="[
          'step-progress__step',
          { 'step-progress__step--valid': index < currentStep },
          { 'step-progress__step--active-not-process':  (index === currentStep && !isProcessing) },
          { 'step-progress__step--active':  (index === currentStep && isProcessing) },
          { 'blink-active': blinkActive && index === currentStep }
        ]"
        @mouseenter="showStepOnHover ? setHoveredStep(index) : null"
        @mouseleave="showStepOnHover ? setHoveredStep(null) : null">
        <span :class="[{ 'blink-animation': blinkActive && index === currentStep }]">{{ index + 1 }}</span>
        <div
          class="step-progress__step-icon"
          :class="[
            { [iconClass]: index < currentStep || (index === currentStep && !currentIconClass) },
            { [currentIconClass]: (index === currentStep && currentIconClass)}
          ]"
        ></div>
        <template v-if="showStepOnHover">
          <div
            v-if="hoveredStep === index"
            :class="[
              'step-progress__bubble',
              {
                'step-progress__bubble--first': index === 0,
                'step-progress__bubble--last': index === steps.length - 1,
                'step-progress__bubble--middle': index > 0 && index < steps.length - 1
              }
            ]">
            {{ step }}
          </div>
        </template>
        <template v-else>
          <div v-if="!showStatusInBubble" class="step-progress__step-label">{{ step }}</div>
          <div
            v-if="index === getBubblePointTo(currentStep) && (showStatusInBubble || bubbleText)"
            :class="[
              'step-progress__bubble',
              {
                'step-progress__bubble--first': index === 0,
                'step-progress__bubble--last': index === steps.length - 1,
                'step-progress__bubble--middle': index > 0 && index < steps.length - 1
              }
            ]">
            <template v-if="bubbleText">
              {{ bubbleText }}
            </template>
            <template v-else>
              {{ step }}
            </template>
          </div>
        </template>
      </div>
    </div>
    <div
      class="step-progress__wrapper-after"
      :style="{
        transform: 'scaleX(' + scaleX + ') translateY(-50%) perspective(1000px)',
        'background-color': activeColor,
        height: lineThickness + 'px'
      }"></div>
  </div>
</template>

<script>
export default {
  name: 'StepProgress',
  props: {
    steps: {
      type: Array,
      default() { return []; },
      validator(val) {
        return val && val.length > 0;
      }
    },
    currentStep: {
      type: Number,
      default: 0
    },
    iconClass: {
      type: String,
      default: 'fa fa-check'
    },
    currentIconClass: {
      type: String,
      default: ''
    },
    activeColor: {
      type: String,
      default: 'red'
    },
    currentStepColor: {
      type: String,
      default: ''
    },
    passiveColor: {
      type: String,
      default: 'gray'
    },
    activeThickness: {
      type: Number,
      default: 5
    },
    passiveThickness: {
      type: Number,
      default: 5
    },
    lineThickness: {
      type: Number,
      default: 12
    },
    isProcessing: {
      type: Boolean,
      default: true
    },
    blinkActive: {
      type: Boolean,
      default: false
    },
    showStatusInBubble: {
      type: Boolean,
      default: false
    },
    bubblePointTo: {
      type: Number,
      default: null
    },
    bubbleText: {
      type: String,
      default: ''
    },
    showStepOnHover: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      hoveredStep: null
    };
  },
  computed: {
    scaleX() {
      let step = this.currentStep;
      if (step < 0) {
        step = 0;
      } else if (step >= this.steps.length) {
        step = this.steps.length - 1;
      }
      return step / (this.steps.length - 1);
    }
  },
  methods: {
    getBubblePointTo(currentStep) {
      return this.bubblePointTo || currentStep;
    },
    setHoveredStep(index) {
      this.hoveredStep = index;
    }
  },
};
</script>

<style>
.step-progress__wrapper {
  width: 90%;
  margin: 0 auto;
  position: relative;
}

.step-progress__wrapper-before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  height: 12px;
  width: 100%;
  background-color: gray;
  transform: translateY(-50%) perspective(1000px);
}

.step-progress__wrapper-after {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  height: 12px;
  width: 100%;
  background-color: red;
  transform: scaleX(0) translateY(-50%) perspective(1000px);
  transform-origin: left center;
  transition: transform 0.5s ease;
}

.step-progress__bar {
  width: 100%;
  display: flex;
  height: 100px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
}

.step-progress__step {
  z-index: 2;
  position: relative;
  --activeColor: red;
  --currentStepColor: red;
  --passiveColor: gray;
  --activeBorder: 5px;
  --passiveBorder: 5px;
  --stepColor: var(--passiveColor); /* Default to passiveColor */  
}

.step-progress__step--valid {
  --stepColor: var(--activeColor); /* Default to activeColor */  
}

.step-progress__step--active {
  --stepColor: var(--currentStepColor); /* Use activeColor for valid or active steps */
}

.step-progress__step--active-not-process {
  --stepColor: var(--currentStepColor); /* Use activeColor for valid or active steps */
}

.step-progress__step span {
  color: var(--passiveColor);
  transition: 0.3s ease;
  display: block;
  font-size: 50px;
  transform: translate3d(0, 0, 0) scale(1) perspective(1000px);
  font-weight: 900;
  text-align: center;
  opacity: 1;
}

@media (max-width: 767px) {
  .step-progress__step span {
    font-size: 28px;
  }
}

.step-progress__step--active span {
  color: var(--currentStepColor);
}

.step-progress__step--active-not-process span {
  color: var(--currentStepColor);
  opacity: 0;
  transform: translate3d(0, 0, 0) scale(2) perspective(1000px);
}

.step-progress__step--valid span {
  color: var(--activeColor);
  opacity: 0;
  transform: translate3d(0, 0, 0) scale(2) perspective(1000px);
}

.step-progress__step--active .step-progress__step-label {
  color: var(--currentStepColor);
}

.step-progress__step--active-not-process .step-progress__step-label {
  color: var(--currentStepColor);
}

.step-progress__step--valid .step-progress__step-label {
  color: var(--activeColor);
}

.step-progress__step--active .step-progress__step-icon {
  opacity: 1;
}

.step-progress__step--active-not-process .step-progress__step-icon {
  opacity: 1;
  transform: translate3d(-50%, -50%, 0) scale(1) perspective(1000px);
}

.step-progress__step--valid .step-progress__step-icon {
  opacity: 1;
  transform: translate3d(-50%, -50%, 0) scale(1) perspective(1000px);
}

.step-progress__step:after {
  content: "";
  position: absolute;
  z-index: -1;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) perspective(1000px);
  width: 75px;
  height: 75px;
  background-color: #fff;
  border-radius: 50%;
  border: var(--passiveBorder) solid var(--passiveColor);
  transition: 0.3s ease;
}

@media (max-width: 767px) {
  .step-progress__step:after {
    width: 40px;
    height: 40px;
  }
}

.step-progress__step--active:after {
  border: var(--activeBorder) solid var(--currentStepColor);
}

.step-progress__step--active-not-process:after {
  background-color: var(--currentStepColor);
  border: var(--activeBorder) solid var(--currentStepColor);
}

.blink-active:after {
  animation: blink 2s ease-in-out infinite;
}

.step-progress__step--valid:after {
  background-color: var(--activeColor);
  border: var(--activeBorder) solid var(--activeColor);
}

.step-progress__step-label {
  position: absolute;
  top: calc(100% + 25px);
  left: 50%;
  transform: translateX(-50%) perspective(1000px);
  white-space: nowrap;
  font-size: 18px;
  font-weight: 600;
  color: gray;
  transition: 0.3s ease;
}

.step-progress__step-icon {
  font-size: 36px;
  color: #fff;
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.3s ease;
  opacity: 0;
  transform: translate3d(-50%, -50%, 0) scale(0) perspective(1000px);
}

@media (max-width: 767px) {
  .step-progress__step-icon {
    font-size: 22px;
  }
}

.step-progress__bubble {
  position: absolute;
  bottom: calc(100% + 20px);
  /* Use the step's color (either --activeColor or --passiveColor) */
  background-color: var(--stepColor);
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 3;
  max-width: 200px;
  text-align: center;
}

.step-progress__bubble:after {
  content: '';
  position: absolute;
  top: 100%;
  border: 8px solid transparent;
  /* Use the step's color for the triangle */
  border-top-color: var(--stepColor);
}

.step-progress__bubble--first {
  left: 0;
}

.step-progress__bubble--first:after {
  left: 20px;
}

.step-progress__bubble--last {
  right: 0;
}

.step-progress__bubble--last:after {
  right: 20px;
}

.step-progress__bubble--middle {
  left: 50%;
  transform: translateX(-50%);
}

.step-progress__bubble--middle:after {
  left: 50%;
  transform: translateX(-50%);
}

@media (max-width: 767px) {
  .step-progress__bubble {
    font-size: 14px;
    padding: 6px 12px;
    max-width: 150px;
  }

  .step-progress__bubble:after {
    border-width: 6px;
  }

  .step-progress__bubble--first:after {
    left: 15px;
  }

  .step-progress__bubble--last:after {
    right: 15px;
  }
}

.blink-animation {
  animation: blink 2s ease-in-out infinite; /* Add blinking animation */
}

/* Blinking animation */
@keyframes blink {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}

</style>
